// src/lib/displayPrefs.ts
//
// App-wide text/UI scale ("accessibility text size"). Because the UI is styled
// almost entirely with Tailwind's rem-based utilities (text-*, padding, gaps,
// w-56, max-w-*), setting font-size on the <html> element scales the WHOLE
// layout proportionally — text, tables, panels — with no per-component work.
//
// Kept within safe bounds (100%–125%) so higher scales enlarge content without
// breaking layouts. 'normal' is the always-available way to switch back.
//
// Stored per-browser in localStorage. Currently the control is exposed to
// system admins only (LandingPage) as a preview; the applied scale is local to
// whichever browser set it, so non-admin users are never affected.

export type UiScale = 'normal' | 'large' | 'xlarge';

const KEY = 'geni.uiScale';

export const SCALES: UiScale[] = ['normal', 'large', 'xlarge'];

export const SCALE_LABEL: Record<UiScale, string> = {
  normal: 'Normal',
  large: 'Large',
  xlarge: 'Extra large',
};

const SCALE_PCT: Record<UiScale, string> = {
  normal: '100%',
  large: '112.5%',
  xlarge: '125%',
};

export function getStoredScale(): UiScale {
  try {
    const v = localStorage.getItem(KEY) as UiScale | null;
    return v && SCALES.includes(v) ? v : 'normal';
  } catch {
    return 'normal';
  }
}

/** Apply a scale to the document root (no persistence). */
export function applyScale(scale: UiScale): void {
  try {
    document.documentElement.style.fontSize = SCALE_PCT[scale];
    // Tables widen/narrow with the scale — ask the top-scrollbar enhancer to
    // re-measure (ResizeObserver alone can be throttled in background tabs).
    window.dispatchEvent(new Event('geni:remeasure'));
  } catch {
    /* SSR / no document — ignore */
  }
}

/** Persist and apply a scale immediately. */
export function storeScale(scale: UiScale): void {
  try {
    localStorage.setItem(KEY, scale);
  } catch {
    /* storage disabled — apply for this session anyway */
  }
  applyScale(scale);
}
